/**
 * PVConfigForm - Photovoltaic configuration form.
 * Implements Tasks 2-5: installation date, net-metering warning,
 * installation cost presets, RCE scenario picker.
 */

import { Stack, Text, Slider, Select, Radio, Box, Alert, NumberInput, Group, Button } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import type { ProsumerFormValues, InstallationCostPreset, RCEPriceScenario } from '../../types';

export interface PVConfigFormProps {
  form: UseFormReturnType<ProsumerFormValues>;
}

// Installation cost per kWp for each preset
const PRESET_COSTS: Record<Exclude<InstallationCostPreset, 'CUSTOM'>, number> = {
  ECONOMY: 4500,
  STANDARD: 7000,
  PREMIUM: 10000,
};

// RCE price values for each preset scenario
const RCE_VALUES: Record<Exclude<RCEPriceScenario, 'CUSTOM'>, number> = {
  CONSERVATIVE: 0.15,
  EXPECTED: 0.22,
  OPTIMISTIC: 0.30,
};

// Net-metering cutoff date: April 1, 2022
const NET_METERING_CUTOFF = '2022-04-01';

export function PVConfigForm({ form }: PVConfigFormProps) {
  const { pvConfig } = form.values;
  const capacityKwp = pvConfig.capacityKwp;

  // Task 3: show warning when NET_METERING is selected AND installation date is on or after the cutoff
  const showNetMeteringWarning =
    pvConfig.settlementType === 'NET_METERING' &&
    pvConfig.installationDate >= NET_METERING_CUTOFF;

  // Task 4: handle preset selection
  function handlePresetClick(preset: InstallationCostPreset) {
    form.setFieldValue('pvConfig.installationCostPreset', preset);
    if (preset !== 'CUSTOM') {
      form.setFieldValue('pvConfig.installationCostPln', capacityKwp * PRESET_COSTS[preset]);
    }
  }

  // Task 5: handle RCE scenario selection
  function handleRceScenarioClick(scenario: RCEPriceScenario) {
    form.setFieldValue('pvConfig.rcePriceScenario', scenario);
    if (scenario !== 'CUSTOM') {
      form.setFieldValue('pvConfig.rcePriceOverridePln', RCE_VALUES[scenario]);
    } else {
      form.setFieldValue('pvConfig.rcePriceOverridePln', null);
    }
  }

  return (
    <Stack gap="sm">
      {/* Capacity slider */}
      <Box>
        <Text size="sm" fw={500}>Moc instalacji (kWp)</Text>
        <Slider
          min={1}
          max={50}
          step={0.5}
          value={capacityKwp}
          onChange={(val) => {
            form.setFieldValue('pvConfig.capacityKwp', val);
            // Recalculate cost if a non-custom preset is selected
            if (pvConfig.installationCostPreset !== 'CUSTOM') {
              form.setFieldValue(
                'pvConfig.installationCostPln',
                val * PRESET_COSTS[pvConfig.installationCostPreset]
              );
            }
          }}
          label={(val) => `${val} kWp`}
          mt={4}
        />
        <Text size="xs" c="dimmed" mt={6}>
          W polskich warunkach instalacja 1 kWp produkuje średnio 900–1 100 kWh rocznie.
          Im wyższa moc, tym więcej energii trafia do autokonsumpcji lub jest oddawane do sieci,
          co bezpośrednio przekłada się na oszczędności na rachunku.
        </Text>
      </Box>

      {/* Orientation */}
      <Select
        label="Orientacja dachu"
        description="Południe daje najwyższą roczną produkcję (100%). Wschód i zachód generują ok. 80–85% tej wartości, ale lepiej pokrywają zużycie poranne lub wieczorne. Kalkulator uwzględnia profil nasłonecznienia przy wyznaczaniu godzinowej autokonsumpcji."
        data={[
          { value: 'SOUTH', label: 'Południe' },
          { value: 'EAST', label: 'Wschód' },
          { value: 'WEST', label: 'Zachód' },
          { value: 'FLAT', label: 'Płaski' },
        ]}
        value={pvConfig.orientation}
        onChange={(val) => val && form.setFieldValue('pvConfig.orientation', val as any)}
      />

      {/* Task 2: Installation date */}
      <Box>
        <Text size="sm" fw={500} mb={4}>Data instalacji (opcjonalnie)</Text>
        <input
          type="date"
          value={pvConfig.installationDate}
          onChange={(e) => form.setFieldValue('pvConfig.installationDate', e.target.value)}
          style={{ padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px', width: '100%' }}
        />
        <Text size="xs" c="dimmed" mt={4}>
          Instalacje zarejestrowane przed 1 kwietnia 2022 r. mogą korzystać z net-metering.
          Nowe instalacje rozliczane są w systemie net-billing.
        </Text>
      </Box>

      {/* Settlement type */}
      <Radio.Group
        label="Typ rozliczenia"
        description="Net-billing (od 2022 r.) wycenia oddaną energię po bieżącej cenie rynkowej RCE – wysokość zwrotu zależy od cen giełdowych. Net-metering (instalacje sprzed 2022 r.) zamienia oddane kWh na depozyt energetyczny do odbioru w ciągu 12 miesięcy; instalacje ≤10 kWp odzyskują 80% (stosunek 1:0,8), a instalacje >10 kWp odzyskują 70% (stosunek 1:0,7) oddanej energii."
        value={pvConfig.settlementType}
        onChange={(val) => form.setFieldValue('pvConfig.settlementType', val as any)}
      >
        <Stack gap="xs" mt={4}>
          <Radio value="NET_BILLING" label="Net-billing" />
          <Radio value="NET_METERING" label="Net-metering" />
        </Stack>
      </Radio.Group>

      {/* Task 3: Net-metering post-2022 warning */}
      {showNetMeteringWarning && (
        <Alert color="orange" role="alert">
          Net-metering dostępny tylko dla instalacji zarejestrowanych przed 1 kwietnia 2022 r.
          Nowe instalacje rozliczane są wyłącznie w systemie net-billing.
        </Alert>
      )}

      {/* RCE pricing choice (only for NET_BILLING) */}
      {pvConfig.settlementType === 'NET_BILLING' && (
        <Radio.Group
          label="Wycena RCE"
          description="Wycena godzinowa dopasowuje cenę RCE do każdej godziny produkcji i poboru, dając dokładniejszy wynik finansowy. Wycena miesięczna używa uśrednionej ceny RCE dla danego miesiąca – szybsze obliczenie, odpowiednie gdy nie ma danych godzinowych."
          value={pvConfig.rcePricingChoice}
          onChange={(val) => form.setFieldValue('pvConfig.rcePricingChoice', val as any)}
        >
          <Stack gap="xs" mt={4}>
            <Radio value="HOURLY" label="Godzinowy" />
            <Radio value="MONTHLY" label="Miesięczny" />
          </Stack>
        </Radio.Group>
      )}

      {/* Task 5: RCE scenario picker (only for NET_BILLING) */}
      {pvConfig.settlementType === 'NET_BILLING' && (
        <Box>
          <Text size="sm" fw={500} mb={4}>Scenariusz cen RCE (dla net-billing)</Text>
          <Text size="xs" c="dimmed" mb={6}>
            Cena RCE (Rynkowa Cena Energii) to giełdowa wycena energii.
            Wahała się między 0,10–0,35 PLN/kWh w 2024–2025 (źródło: PSE).
          </Text>
          <Group gap="xs">
            {(['CONSERVATIVE', 'EXPECTED', 'OPTIMISTIC', 'CUSTOM'] as RCEPriceScenario[]).map(
              (scenario) => {
                const labels: Record<RCEPriceScenario, string> = {
                  CONSERVATIVE: 'Konserw. 0,15',
                  EXPECTED: 'Bieżący 0,22',
                  OPTIMISTIC: 'Optymist. 0,30',
                  CUSTOM: 'Własny',
                };
                return (
                  <Button
                    key={scenario}
                    size="xs"
                    variant={pvConfig.rcePriceScenario === scenario ? 'filled' : 'outline'}
                    onClick={() => handleRceScenarioClick(scenario)}
                  >
                    {labels[scenario]}
                  </Button>
                );
              }
            )}
          </Group>
          {pvConfig.rcePriceScenario === 'CUSTOM' && (
            <NumberInput
              label="Własna cena RCE"
              description="Podaj własną cenę RCE w PLN/kWh"
              suffix=" PLN/kWh"
              min={0.01}
              max={1.0}
              step={0.01}
              value={pvConfig.rcePriceOverridePln ?? ''}
              onChange={(val) =>
                form.setFieldValue(
                  'pvConfig.rcePriceOverridePln',
                  typeof val === 'number' ? val : null
                )
              }
              mt={8}
            />
          )}
        </Box>
      )}

      {/* Task 4: Installation cost presets */}
      <Box>
        <Text size="sm" fw={500} mb={4}>Koszt instalacji (opcjonalnie)</Text>
        <Group gap="xs">
          {(['ECONOMY', 'STANDARD', 'PREMIUM', 'CUSTOM'] as InstallationCostPreset[]).map(
            (preset) => {
              const labels: Record<InstallationCostPreset, string> = {
                ECONOMY: 'Ekonomiczny',
                STANDARD: 'Standardowy',
                PREMIUM: 'Premium',
                CUSTOM: 'Własny',
              };
              return (
                <Button
                  key={preset}
                  size="xs"
                  variant={pvConfig.installationCostPreset === preset ? 'filled' : 'outline'}
                  onClick={() => handlePresetClick(preset)}
                >
                  {labels[preset]}
                </Button>
              );
            }
          )}
        </Group>

        {pvConfig.installationCostPreset !== 'CUSTOM' && pvConfig.installationCostPln > 0 && (
          <Text size="xs" c="dimmed" mt={4}>
            Szacowany koszt: {pvConfig.installationCostPln.toLocaleString('pl-PL')} PLN
            ({capacityKwp} kWp × {PRESET_COSTS[pvConfig.installationCostPreset].toLocaleString('pl-PL')} PLN/kWp)
          </Text>
        )}

        {pvConfig.installationCostPreset === 'CUSTOM' && (
          <NumberInput
            label="Własny koszt instalacji"
            description="Ceny orientacyjne. Dokładna wycena: poproś instalatora o bezpłatną ofertę."
            suffix=" PLN"
            min={0}
            step={1000}
            value={pvConfig.installationCostPln}
            onChange={(val) =>
              form.setFieldValue(
                'pvConfig.installationCostPln',
                typeof val === 'number' ? val : 0
              )
            }
            mt={8}
          />
        )}

        {pvConfig.installationCostPreset !== 'CUSTOM' && (
          <Text size="xs" c="dimmed" mt={4}>
            Ceny orientacyjne (~{PRESET_COSTS[pvConfig.installationCostPreset].toLocaleString('pl-PL')} PLN/kWp).
            Dokładna wycena: poproś instalatora o bezpłatną ofertę.
          </Text>
        )}
      </Box>
    </Stack>
  );
}
